/**
 * Depth-limited blur utility that takes samples using a Poisson disk.
 */
import * as THREE from "three";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { DepthLimitedPoissonBlurShader, PoissonBlurShaderUtils } from "./DepthLimitedPoissonBlurShader";
import type { DepthNormalPass } from "./DepthNormalPass";
import { screenSpaceViewNormalPrepareShader, screenSpaceViewPositionPrepareShader } from "./screenSpaceHelpersGLSL";

export interface DepthLimitedPoissonBlurParams {
  // These parameters are mapped to shader defines, changing them can cause a recompile.
  kernelSamples: number;
  useNormalTexture: boolean;
}

export class DepthLimitedPoissonBlur {
  readonly params: DepthLimitedPoissonBlurParams;

  private blurMaterial: THREE.ShaderMaterial;
  private fsQuad: FullScreenQuad;

  private prevKernelSamples: number = -1;

  constructor() {
    let useNormalTexture = DepthLimitedPoissonBlurShader.defines["NORMAL_TEXTURE"] === 1;
    this.params = {
      kernelSamples: 6, // number of blur samples taken is double this value
      get useNormalTexture(): boolean {
        return useNormalTexture;
      },
      set useNormalTexture(value: boolean) {
        if (useNormalTexture === value) {
          return;
        }
        useNormalTexture = value;
        this.blurMaterial.defines["NORMAL_TEXTURE"] = this.params.useNormalTexture ? 1 : 0;
        this.blurMaterial.needsUpdate = true;
      },
    };

    this.blurMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(DepthLimitedPoissonBlurShader.uniforms),
      defines: { ...DepthLimitedPoissonBlurShader.defines },
      vertexShader: DepthLimitedPoissonBlurShader.vertexShader,
      fragmentShader: DepthLimitedPoissonBlurShader.fragmentShader,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "DepthLimitedPoissonBlur blurMaterial",
    });
    this.blurMaterial.name = "blur";

    this.fsQuad = new FullScreenQuad();
  }

  blur(
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    sourceTexture: THREE.Texture,
    finalRenderTarget: THREE.WebGLRenderTarget,
    depthNormalPass: DepthNormalPass,
    blurColorMask: [boolean, boolean, boolean, boolean],
  ) {
    const blurMaterial = this.blurMaterial;

    this.params.kernelSamples = Math.floor(this.params.kernelSamples);
    if (this.prevKernelSamples !== this.params.kernelSamples) {
      PoissonBlurShaderUtils.configureKernel(blurMaterial, this.params.kernelSamples);
      this.prevKernelSamples = this.params.kernelSamples;
    }

    screenSpaceViewPositionPrepareShader(blurMaterial, camera, depthNormalPass);
    screenSpaceViewNormalPrepareShader(blurMaterial, depthNormalPass);

    blurMaterial.uniforms["tDiffuse"].value = sourceTexture;

    blurMaterial.uniforms["invSize"].value.set(1.0 / finalRenderTarget.width, 1.0 / finalRenderTarget.height);

    blurMaterial.uniforms["colorMask"].value = blurColorMask;

    this.renderPass(renderer, blurMaterial, finalRenderTarget);
  }

  private renderPass(renderer, passMaterial, renderTarget) {
    renderer.setRenderTarget(renderTarget);

    renderer.setClearColor(0xffffff);
    renderer.setClearAlpha(1.0);
    renderer.clear();

    this.fsQuad.material = passMaterial;
    this.fsQuad.render(renderer);
  }

  dispose() {
    this.blurMaterial.dispose();
  }
}
